import { useCallback, useEffect, useMemo, useState } from 'react';

import { createSource, updateSource } from '../lib/actions';
import { compactCount, groupUrl, platformLabel, shortDate } from '../lib/format';
import ActionIcon from '../components/ActionIcon';
import PlatformIcon from '../components/PlatformIcon';
import { supabase } from '../lib/supabase';
import type { Source } from '../lib/types';

/**
 * The watchlist, as one section of Setup.
 *
 * Two of the columns carry the weight, and both are about what the reply is allowed to say.
 * `link_allowed` stops a link going into a community that forbids links, and
 * `solicitation_allowed` stops pitch language where commercial mention is unwelcome. Reading a
 * community's own rules is still the operator's job - those pages cannot be read anonymously -
 * but it is no longer tracked here: the app records the two answers those rules produce, and
 * `policy.ts` defaults to the safe direction, so an unread community fails closed rather than
 * open.
 */

const PLATFORMS = [
  'facebook',
  'youtube',
  'discourse',
  'stackexchange',
  'hackernews',
  'rss',
  'email',
];

type Filter = 'all' | 'enabled' | 'disabled';

interface Props {
  actor: string;
  /**
   * Tells `Setup` that a watchlist row was written, so the sections that read this table reload.
   *
   * The Scan panel above derives what each lane searches for from the rows here, so switching a
   * source on is not a change that stays in this section. Nothing publishes `nestmatch.sources` to
   * realtime, so this callback is the only wire between the two.
   */
  onWatchlistChanged: () => void;
}

interface NewSource {
  platform: string;
  identifier: string;
  display_name: string;
  daily_cap: number;
  min_minutes_between_replies: number;
  link_allowed: boolean;
  solicitation_allowed: boolean;
  enabled: boolean;
  notes: string;
}

const BLANK: NewSource = {
  platform: 'facebook',
  identifier: '',
  display_name: '',
  daily_cap: 3,
  min_minutes_between_replies: 30,
  link_allowed: false,
  solicitation_allowed: false,
  enabled: false,
  notes: '',
};

/**
 * Public or Private for display, derived from `login_required` rather than stored twice: that
 * column is what decides the lane (migration 0013), so the badge and the actor can never
 * disagree. Only a group row has a privacy - `fb/search` and future forum rows get a dash.
 */
function privacyOf(source: Source): 'Public' | 'Private' | null {
  if (!source.identifier.startsWith('fb/group/')) return null;
  return source.login_required ? 'Private' : 'Public';
}

function PrivacyBadge({ source }: { source: Source }) {
  const privacy = privacyOf(source);
  if (!privacy) return <span className="muted">—</span>;
  // Amber for login-walled: the row costs ~7x to read and needs the cookie lane before it runs.
  return <span className={`badge${privacy === 'Private' ? ' warn' : ''}`}>{privacy}</span>;
}

/**
 * The community's name, pointed at the community itself.
 *
 * A Facebook row's identifier is a URL one derivation away, and the first thing an operator does
 * with a row is open the group - to read its rules before trusting it, or to see what it looks
 * like now. A row with no derivable URL (a hand-added source) stays plain text rather than
 * becoming a dead link.
 */
function CommunityLink({ source }: { source: Source }) {
  const name = source.display_name ?? source.identifier;
  const url = groupUrl(source.platform, source.identifier);
  if (!url) return <span>{name}</span>;
  return (
    <a className="source-link" href={url} target="_blank" rel="noreferrer noopener">
      {name}
    </a>
  );
}

export default function SourcesSection({ actor, onWatchlistChanged }: Props) {
  const [sources, setSources] = useState<Source[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<NewSource>(BLANK);
  /** The row whose note is open for editing. One at a time, and none of them by default. */
  const [editingNotes, setEditingNotes] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: readError } = await supabase
      .from('sources')
      .select('*')
      .order('platform', { ascending: true })
      .order('identifier', { ascending: true });

    if (readError) setError(readError.message);
    else setSources((data ?? []) as Source[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(
    () => ({
      all: sources.length,
      enabled: sources.filter((source) => source.enabled).length,
      disabled: sources.filter((source) => !source.enabled).length,
    }),
    [sources],
  );

  const visible = useMemo(() => {
    switch (filter) {
      case 'enabled':
        return sources.filter((source) => source.enabled);
      case 'disabled':
        return sources.filter((source) => !source.enabled);
      default:
        return sources;
    }
  }, [sources, filter]);

  async function patch(source: Source, changes: Partial<Source>, optimistic = true): Promise<boolean> {
    setStatus(null);
    setError(null);
    if (optimistic) {
      setSources((current) =>
        current.map((row) => (row.id === source.id ? { ...row, ...changes } : row)),
      );
    }
    const result = await updateSource(actor, source.id, changes as Record<string, unknown>);
    if (!result.ok) {
      setError(result.error ?? 'Could not save that.');
      await load();
      return false;
    }
    // After the write, never before it: a sibling section re-reads the table, so signalling
    // optimistically would have it fetch the value this change replaces. Only the three columns
    // the scan panel derives subjects from are worth a reload - saving a cap or a note is not a
    // change to what a lane searches for.
    if ('enabled' in changes || 'platform' in changes || 'identifier' in changes) {
      onWatchlistChanged();
    }
    return true;
  }

  async function toggle(source: Source, field: 'enabled' | 'link_allowed' | 'solicitation_allowed') {
    await patch(source, { [field]: !source[field] } as Partial<Source>);
  }

  async function add() {
    setStatus(null);
    setError(null);
    if (!form.identifier.trim()) {
      setError('An identifier is required — for a Facebook group, `fb/group/<slug-or-id>`.');
      return;
    }
    setBusy(true);
    const result = await createSource(actor, {
      platform: form.platform,
      identifier: form.identifier.trim(),
      display_name: form.display_name.trim() || null,
      link_allowed: form.link_allowed,
      solicitation_allowed: form.solicitation_allowed,
      daily_cap: Number(form.daily_cap) || 0,
      min_minutes_between_replies: Number(form.min_minutes_between_replies) || 0,
      notes: form.notes.trim() || null,
      enabled: form.enabled,
    });
    setBusy(false);
    if (result.ok) {
      setStatus(
        form.enabled ? 'Added and enabled.' : 'Added, disabled. Switch it on when ready.',
      );
      setForm({ ...BLANK, platform: form.platform });
      await load();
      // A new row is a new candidate subject even before it is switched on, and the scan panel
      // cannot see it until it reloads.
      onWatchlistChanged();
    } else {
      setError(result.error ?? 'Could not add the source.');
    }
  }

  return (
    <section className="setup-section">
      <h3>Watchlist</h3>
      <p className="muted small">
        One row per community the actors look at. Switch a row on to let them search there; Members
        and Posts are what the group-search actor measured on the date shown. Links and Pitch record
        what that community allows — the guardrail reads both before a reply goes out.
      </p>

      {status ? (
        <p className="notice" role="status">
          {status}
        </p>
      ) : null}
      {error ? <p className="error">{error}</p> : null}

      {sources.length === 0 ? (
        <p className="muted">
          No sources yet. Build the list in <code>docs/policy/community-whitelist.md</code>.
        </p>
      ) : (
        <>
          <div className="filters">
            <button
              type="button"
              className={filter === 'all' ? 'active-filter' : undefined}
              onClick={() => setFilter('all')}
            >
              All {counts.all}
            </button>
            <button
              type="button"
              className={filter === 'enabled' ? 'active-filter' : undefined}
              onClick={() => setFilter('enabled')}
            >
              Enabled {counts.enabled}
            </button>
            <button
              type="button"
              className={filter === 'disabled' ? 'active-filter' : undefined}
              onClick={() => setFilter('disabled')}
            >
              Disabled {counts.disabled}
            </button>
          </div>

          <div className="table-scroll">
            <table className="wide">
              <thead>
                <tr>
                  <th>Community</th>
                  <th>Privacy</th>
                  <th className="num">Members</th>
                  <th className="num">Posts / mo</th>
                  <th>Measured</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((source) => (
                  <tr key={source.id}>
                    <td>
                      <div className="source-head">
                        <PlatformIcon platform={source.platform} size={14} />
                        <CommunityLink source={source} />
                        {source.notes ? (
                          editingNotes === source.id ? (
                            <input
                              type="text"
                              autoFocus
                              className="note-edit"
                              defaultValue={source.notes}
                              aria-label={`Note for ${source.identifier}`}
                              onKeyDown={(event) => {
                                if (event.key === 'Escape') setEditingNotes(null);
                              }}
                              onBlur={(event) => {
                                const next = event.target.value.trim() || null;
                                setEditingNotes(null);
                                if (next === source.notes) return;
                                void patch(source, { notes: next }, false).then((ok) => {
                                  if (ok) setStatus('Notes saved.');
                                });
                              }}
                            />
                          ) : (
                            <button
                              type="button"
                              className="link note-open"
                              title={source.notes}
                              aria-label={`Note for ${source.identifier}`}
                              onClick={() => setEditingNotes(source.id)}
                            >
                              <ActionIcon name="note" size={13} />
                            </button>
                          )
                        ) : null}
                      </div>
                    </td>

                    <td>
                      <PrivacyBadge source={source} />
                    </td>
                    <td
                      className="num"
                      title={
                        source.members_count != null
                          ? `${source.members_count.toLocaleString('en-GB')} members`
                          : undefined
                      }
                    >
                      {compactCount(source.members_count)}
                    </td>
                    <td
                      className="num"
                      title={
                        source.posts_last_month != null
                          ? `${source.posts_last_month.toLocaleString('en-GB')} posts in the last 30 days (10,000 is the actor's display cap)`
                          : undefined
                      }
                    >
                      {compactCount(source.posts_last_month)}
                    </td>
                    <td className="muted small">
                      {source.measured_at ? shortDate(source.measured_at) : '—'}
                    </td>

                    <td>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={source.enabled}
                          aria-label={`Enabled: ${source.identifier}`}
                          onChange={() => void toggle(source, 'enabled')}
                        />
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="card" style={{ marginTop: 24 }}>
        <h3>Add source</h3>
        <p className="muted small">
          The community to watch. How replies are written, and where they may carry a link, is the
          Worker’s job — there is nothing to configure here.
        </p>

        <div className="grid">
          <div>
            <label htmlFor="new-platform">Platform</label>
            <select
              id="new-platform"
              value={form.platform}
              onChange={(event) => setForm({ ...form, platform: event.target.value })}
            >
              {PLATFORMS.map((platform) => (
                <option key={platform} value={platform}>
                  {platformLabel(platform)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="new-identifier">Identifier</label>
            <input
              id="new-identifier"
              type="text"
              value={form.identifier}
              placeholder="r/AmericanExpatsUK"
              onChange={(event) => setForm({ ...form, identifier: event.target.value })}
            />
          </div>
          <div>
            <label htmlFor="new-name">Display name</label>
            <input
              id="new-name"
              type="text"
              value={form.display_name}
              onChange={(event) => setForm({ ...form, display_name: event.target.value })}
            />
          </div>
          <div>
            <label htmlFor="new-notes">Notes</label>
            <input
              id="new-notes"
              type="text"
              value={form.notes}
              placeholder="links banned; modmail first"
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
            />
          </div>
        </div>

        <label className="toggle" style={{ marginTop: 12 }}>

          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
          />
          <span>Enabled — start scanning it (spends credit)</span>
        </label>

        <button type="button" onClick={() => void add()} disabled={busy}>
          {busy ? 'Adding…' : 'Add source'}
        </button>
      </div>
    </section>
  );
}
